import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { IconButton } from "../../components/IconButton";
import FriendsBlankState from "../components/BlankState";
import FriendRow from "../components/FriendRow";
import { useAcceptFriendRequest } from "../hooks/useAcceptFriendRequest";
import { type WorkerPublicProfile } from "../hooks/useGetWorkerPublicProfile";
import { useRejectFriendRequest } from "../hooks/useRejectFriendRequest";
import WorkWithFriendsContext from "../WorkWithFriendsContext";

interface Props {
  friends: WorkerPublicProfile[];
}
export default function TabInvites(props: Props) {
  const { friends } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const { setSelectedTab } = useContext(WorkWithFriendsContext);

  const { mutate: rejectFriendRequest, isLoading: isLoadingRejection } = useRejectFriendRequest({
    onSuccess: () => {
      showSuccessToast("Friend request rejected");
      void queryClient.invalidateQueries({ queryKey: ["inbound-friends-requests"] });
    },
    onError: () => {
      showErrorToast("Failed to reject friend request. Please try again.");
    },
  });

  const { mutate: acceptFriendRequest, isLoading: isLoadingAcceptance } = useAcceptFriendRequest({
    onSuccess: () => {
      showSuccessToast("Friend request accepted");
      void queryClient.invalidateQueries({ queryKey: ["friends-list"] });
      void queryClient.invalidateQueries({ queryKey: ["inbound-friends-requests"] });
      if (friends.length <= 1) {
        setSelectedTab("friends");
      }
    },
    onError: () => {
      showErrorToast("Failed to accept friend request. Please try again.");
    },
  });

  if (!friends?.length) {
    return <FriendsBlankState />;
  }

  const isLoading = isLoadingRejection || isLoadingAcceptance;

  return (
    <Stack spacing={6}>
      {friends.map((friend) => (
        <FriendRow
          key={friend.id}
          friend={friend}
          actions={
            <>
              <IconButton
                title="Confirm"
                disabled={isLoading}
                size="small"
                variant="contained"
                iconType="user-add"
                onClick={() => {
                  acceptFriendRequest(friend.id);
                }}
              />
              <IconButton
                title="Ignore"
                disabled={isLoading}
                size="small"
                variant="outlined"
                iconType="close"
                onClick={() => {
                  rejectFriendRequest(friend.id);
                }}
              />
            </>
          }
        />
      ))}
    </Stack>
  );
}
